import { useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';

import {
  type FieldReferenceInputProps,
  type FieldReferenceMenuItem,
  type Field,
  type AltContext,
  type FieldReferenceMenuProps,
  fieldReferenceMenuHelpers,
  type FieldReferenceDisplayProps,
  getIconMapping,
  typeIconMapping
} from '@pega/cosmos-react-build';

import { loadingTimeoutMS } from '../../core/Progress/Progress.mocks';

export const actions: FieldReferenceDisplayProps['actions'] = [
  {
    id: 'configure',
    text: 'Configure field',
    icon: 'gear',
    onClick: () => {}
  }
];

export const link: FieldReferenceDisplayProps['link'] = {
  href: '#field',
  previewable: true,
  onPreview: () => {},
  onClick: () => {}
};

export const getGeneratedMenuItems = (
  parentItem?: Field | AltContext
): FieldReferenceMenuItem[] => {
  const types = Object.keys(typeIconMapping) as (keyof typeof typeIconMapping)[];
  return [...Array(2000)].map((_, i) => {
    const hasLongName = Math.random() > 0.5;
    const id = `${parentItem?.id ? `${parentItem?.id}-` : ''}${i + 1}`;
    const name = `Field${hasLongName ? ' with a long long long long name' : ''} ${id}`;
    const type = types[Math.floor(Math.random() * types.length)];

    const item: FieldReferenceMenuItem = {
      id,
      name,
      type,
      icon: getIconMapping(type)
    };

    if (type === 'Field reference') {
      item.type = `${name} (reference single)`;
      item.items = [];
      item.selected = false;
    } else if (type === 'Embedded field') {
      item.type = `${name} (reference multi)`;
      item.items = [];
    }

    return item;
  });
};

export const getStaticMenuItems = (parentItem?: Field | AltContext): FieldReferenceMenuItem[] => {
  if (parentItem?.id.endsWith('cloneCase'))
    return [
      {
        id: 'status',
        name: 'Status',
        type: 'Text',
        icon: { set: 'streamline', name: 'dataviz-string-field' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.status' }
        ]
      },
      {
        id: 'description',
        name: 'Description',
        type: 'Paragraph',
        icon: { set: 'streamline', name: 'filetype-text' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.description' }
        ]
      },
      {
        id: 'completed',
        name: 'Completed',
        type: 'Boolean',
        icon: { set: 'streamline', name: 'circle-mixed-left' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.completed' }
        ]
      },
      {
        id: 'releaseDate',
        name: 'Release date',
        type: 'Date',
        icon: { set: 'streamline', name: 'calendar-empty' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.releaseDate' }
        ]
      },
      {
        id: 'lastUpdated',
        name: 'Last updated',
        type: 'Time',
        icon: { set: 'streamline', name: 'clock' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.lastUpdated' }
        ]
      },
      {
        id: 'createdAt',
        name: 'Created at',
        type: 'Date & time',
        icon: { set: 'streamline', name: 'dataviz-datetime-field' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.createdAt' }
        ]
      },
      {
        id: 'availability',
        name: 'Availability',
        type: 'Integer',
        icon: { set: 'streamline', name: 'dataviz-numeric-field' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.availability' }
        ]
      },
      {
        id: 'hoursSpent',
        name: 'Hours spent',
        type: 'Decimal',
        icon: { set: 'streamline', name: 'number-decimal' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.hoursSpent' }
        ]
      },
      {
        id: 'costAnalysis',
        name: 'Cost analysis',
        type: 'Currency',
        icon: { set: 'streamline', name: 'currency' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.costAnalysis' }
        ]
      },
      {
        id: 'contactEmail',
        name: 'Contact email',
        type: 'Email',
        icon: { set: 'streamline', name: 'mail' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.contactEmail' }
        ]
      },
      {
        id: 'contactPhone',
        name: 'Contact phone',
        type: 'Phone',
        icon: { set: 'streamline', name: 'phone' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.contactPhone' }
        ]
      },
      {
        id: 'severity',
        name: 'Severity',
        type: 'Picklist',
        icon: { set: 'streamline', name: 'list' },
        metadata: [
          { name: 'Applies to', value: 'User story' },
          { name: 'ID', value: 'cloneCase.severity' }
        ]
      }
    ];

  if (
    parentItem?.id.endsWith('downDepStories') ||
    parentItem?.id.endsWith('downDepBugs') ||
    parentItem?.id.endsWith('upDepEpics') ||
    parentItem?.id.endsWith('upDepStories') ||
    parentItem?.id.endsWith('upDepBugs')
  )
    return [
      {
        id: 'status',
        name: 'Status',
        type: 'Text',
        icon: { set: 'streamline', name: 'dataviz-string-field' }
      },
      {
        id: 'description',
        name: 'Description',
        type: 'Paragraph',
        icon: { set: 'streamline', name: 'filetype-text' }
      },
      {
        id: 'completed',
        name: 'Completed',
        type: 'Boolean',
        icon: { set: 'streamline', name: 'circle-mixed-left' }
      },
      {
        id: 'release',
        name: 'Release',
        type: 'Release (reference)',
        items: [],
        icon: { set: 'streamline', name: 'case' }
      },
      {
        id: 'product',
        name: 'Product',
        type: 'Product (reference)',
        items: [],
        icon: { set: 'streamline', name: 'case' },
        selected: false
      },
      {
        id: 'releaseDate',
        name: 'Release date',
        type: 'Date',
        icon: { set: 'streamline', name: 'calendar-empty' }
      },
      {
        id: 'lastUpdated',
        name: 'Last updated',
        type: 'Time',
        icon: { set: 'streamline', name: 'clock' }
      },
      {
        id: 'createdAt',
        name: 'Created at',
        type: 'Date & time',
        icon: { set: 'streamline', name: 'dataviz-datetime-field' }
      },
      {
        id: 'availability',
        name: 'Availability',
        type: 'Integer',
        icon: { set: 'streamline', name: 'dataviz-numeric-field' }
      },
      {
        id: 'hoursSpent',
        name: 'Hours spent',
        type: 'Decimal',
        icon: { set: 'streamline', name: 'number-decimal' }
      },
      {
        id: 'costAnalysis',
        name: 'Cost analysis',
        type: 'Currency',
        icon: { set: 'streamline', name: 'currency' }
      },
      {
        id: 'contactEmail',
        name: 'Contact email',
        type: 'Email',
        icon: { set: 'streamline', name: 'mail' }
      },
      {
        id: 'contactPhone',
        name: 'Contact phone',
        type: 'Phone',
        icon: { set: 'streamline', name: 'phone' }
      },
      {
        id: 'severity',
        name: 'Severity',
        type: 'Picklist',
        icon: { set: 'streamline', name: 'list' }
      },
      {
        id: 'verifiedBy',
        name: 'Verified by',
        type: 'User Reference',
        icon: { set: 'streamline', name: 'user' }
      },
      {
        id: 'assignTo',
        name: 'Assign to',
        type: 'Picklist',
        icon: { set: 'streamline', name: 'list' }
      },
      {
        id: 'ASReleaseID',
        name: 'ASIntegration_Infinity ReleaseID',
        type: 'Text',
        icon: { set: 'streamline', name: 'dataviz-string-field' }
      },
      {
        id: 'ASProductID',
        name: 'ASIntegration_Infinity ProductID',
        type: 'Text',
        icon: { set: 'streamline', name: 'dataviz-string-field' }
      },
      {
        id: 'ASBuildID',
        name: 'ASIntegration_Infinity BuildID',
        type: 'Text',
        icon: { set: 'streamline', name: 'dataviz-string-field' }
      },
      {
        id: 'ASFeatureID',
        name: 'ASIntegration_Infinity FeatureID',
        type: 'Text',
        icon: { set: 'streamline', name: 'dataviz-string-field' }
      }
    ];

  if (
    parentItem?.id.endsWith('release') ||
    parentItem?.id.endsWith('product') ||
    parentItem?.id.endsWith('aliasVariables') ||
    parentItem?.id.endsWith('parameters')
  )
    return [
      {
        id: 'status',
        name: 'Status',
        type: 'Text',
        icon: { set: 'streamline', name: 'dataviz-string-field' }
      },
      {
        id: 'description',
        name: 'Description',
        type: 'Paragraph',
        icon: { set: 'streamline', name: 'filetype-text' }
      },
      {
        id: 'completed',
        name: 'Completed',
        type: 'Boolean',
        icon: { set: 'streamline', name: 'circle-mixed-left' }
      },
      {
        id: 'releaseDate',
        name: 'Release date',
        type: 'Date',
        icon: { set: 'streamline', name: 'calendar-empty' }
      },
      {
        id: 'lastUpdated',
        name: 'Last updated',
        type: 'Time',
        icon: { set: 'streamline', name: 'clock' }
      },
      {
        id: 'createdAt',
        name: 'Created at',
        type: 'Date & time',
        icon: { set: 'streamline', name: 'dataviz-datetime-field' }
      },
      {
        id: 'availability',
        name: 'Availability',
        type: 'Integer',
        icon: { set: 'streamline', name: 'dataviz-numeric-field' }
      },
      {
        id: 'hoursSpent',
        name: 'Hours spent',
        type: 'Decimal',
        icon: { set: 'streamline', name: 'number-decimal' }
      },
      {
        id: 'costAnalysis',
        name: 'Cost analysis',
        type: 'Currency',
        icon: { set: 'streamline', name: 'currency' }
      }
    ];

  return [
    {
      id: 'resolutionNote',
      name: 'Resolution note',
      type: 'Paragraph',
      icon: { set: 'streamline', name: 'filetype-text' }
    },
    {
      id: 'preBlockStatus',
      name: 'Pre-blocked status',
      type: 'Text',
      icon: { set: 'streamline', name: 'dataviz-string-field' }
    },
    {
      id: 'sprintResAction',
      name: 'Sprint resolution action',
      type: 'Picklist',
      icon: { set: 'streamline', name: 'list' }
    },
    {
      id: 'cloneCase',
      name: 'Clone case',
      type: 'Embedded field',
      items: [],
      icon: { set: 'streamline', name: 'table-bolt' }
    },
    {
      id: 'downDepStories',
      name: 'Downstream dependency stories',
      type: 'Downstream dependency (reference)',
      items: [],
      icon: { set: 'streamline', name: 'case' },
      selected: false
    },
    {
      id: 'downDepBugs',
      name: 'Downstream dependency bugs',
      type: 'Downstream dependency (reference)',
      items: [],
      icon: { set: 'streamline', name: 'case' },
      selected: false
    },
    {
      id: 'passed',
      name: 'Passed',
      type: 'Integer',
      icon: { set: 'streamline', name: 'dataviz-numeric-field' }
    },
    {
      id: 'notExecuted',
      name: 'Not executed',
      type: 'Integer',
      icon: { set: 'streamline', name: 'dataviz-numeric-field' }
    },
    {
      id: 'upDepEpics',
      name: 'Upstream dependency epics',
      type: 'Upstream dependency (reference)',
      items: [],
      icon: { set: 'streamline', name: 'case' },
      selected: false
    },
    {
      id: 'upDepStories',
      name: 'Upstream dependency stories',
      type: 'Upstream dependency (reference)',
      items: [],
      icon: { set: 'streamline', name: 'case' },
      selected: false
    },
    {
      id: 'upDepBugs',
      name: 'Upstream dependency bugs',
      type: 'Upstream dependency (reference)',
      items: [],
      icon: { set: 'streamline', name: 'case' },
      selected: false
    },
    {
      id: 'status',
      name: 'Status',
      type: 'Text',
      icon: { set: 'streamline', name: 'dataviz-string-field' }
    },
    {
      id: 'description',
      name: 'Description',
      type: 'Paragraph',
      icon: { set: 'streamline', name: 'filetype-text' }
    },
    {
      id: 'completed',
      name: 'Completed',
      type: 'Boolean',
      icon: { set: 'streamline', name: 'circle-mixed-left' }
    },
    {
      id: 'releaseDate',
      name: 'Release date',
      type: 'Date',
      icon: { set: 'streamline', name: 'calendar-empty' }
    },
    {
      id: 'lastUpdated',
      name: 'Last updated',
      type: 'Time',
      icon: { set: 'streamline', name: 'clock' }
    },
    {
      id: 'createdAt',
      name: 'Created at',
      type: 'Date & time',
      icon: { set: 'streamline', name: 'dataviz-datetime-field' }
    },
    {
      id: 'availability',
      name: 'Availability',
      type: 'Integer',
      icon: { set: 'streamline', name: 'dataviz-numeric-field' }
    },
    {
      id: 'hoursSpent',
      name: 'Hours spent',
      type: 'Decimal',
      icon: { set: 'streamline', name: 'number-decimal' }
    },
    {
      id: 'costAnalysis',
      name: 'Cost analysis',
      type: 'Currency',
      icon: { set: 'streamline', name: 'currency' }
    },
    {
      id: 'contactEmail',
      name: 'Contact email',
      type: 'Email',
      icon: { set: 'streamline', name: 'mail' }
    },
    {
      id: 'contactPhone',
      name: 'Contact phone',
      type: 'Phone',
      icon: { set: 'streamline', name: 'phone' }
    },
    {
      id: 'severity',
      name: 'Severity',
      type: 'Picklist',
      icon: { set: 'streamline', name: 'list' }
    },
    {
      id: 'updateUser',
      name: 'Update.User',
      type: 'User Reference',
      icon: { set: 'streamline', name: 'user' }
    },
    {
      id: 'updateUserID',
      name: 'Update.UserID',
      type: 'Text',
      icon: { set: 'streamline', name: 'dataviz-string-field' }
    },
    {
      id: 'updateUserDateTime',
      name: 'Update.DateTime',
      type: 'Date & time',
      icon: { set: 'streamline', name: 'dataviz-datetime-field' }
    }
  ];
};

export const defaultContext: FieldReferenceMenuProps['currentContext'] = {
  id: 'userStory',
  name: 'User story'
};

export const defaultAltContexts: FieldReferenceMenuProps['altContexts'] = [
  {
    id: 'aliasVariables',
    name: 'Aliases and Variables',
    items: []
  },
  {
    id: 'parameters',
    name: 'Parameters',
    items: []
  }
];

export const fieldPaths: Record<string, Field[]> = {
  Text: [
    {
      id: 'bugStatus',
      name: 'Bug status',
      type: 'Text',
      icon: { set: 'streamline', name: 'dataviz-string-field' }
    }
  ],
  Paragraph: [
    {
      id: 'description',
      name: 'Description',
      type: 'Paragraph',
      icon: { set: 'streamline', name: 'filetype-text' }
    }
  ],
  Boolean: [
    {
      id: 'completed',
      name: 'Completed',
      type: 'Boolean',
      icon: { set: 'streamline', name: 'circle-mixed-left' }
    }
  ],
  Date: [
    {
      id: 'releaseDate',
      name: 'Release date',
      type: 'Date',
      icon: { set: 'streamline', name: 'calendar-empty' }
    }
  ],
  Time: [
    {
      id: 'lastUpdated',
      name: 'Last updated',
      type: 'Time',
      icon: { set: 'streamline', name: 'clock' }
    }
  ],
  'Date & time': [
    {
      id: 'createdAt',
      name: 'Created at',
      type: 'Date & time',
      icon: { set: 'streamline', name: 'dataviz-datetime-field' }
    }
  ],
  Integer: [
    {
      id: 'availability',
      name: 'Availability',
      type: 'Integer',
      icon: { set: 'streamline', name: 'dataviz-numeric-field' }
    }
  ],
  Decimal: [
    {
      id: 'hoursSpent',
      name: 'Hours spent',
      type: 'Decimal',
      icon: { set: 'streamline', name: 'number-decimal' }
    }
  ],
  Currency: [
    {
      id: 'costAnalysis',
      name: 'Cost analysis',
      type: 'Currency',
      icon: { set: 'streamline', name: 'currency' }
    }
  ],
  Email: [
    {
      id: 'contactEmail',
      name: 'Contact email',
      type: 'Email',
      icon: { set: 'streamline', name: 'mail' }
    }
  ],
  Phone: [
    {
      id: 'contactPhone',
      name: 'Contact phone',
      type: 'Phone',
      icon: { set: 'streamline', name: 'phone' }
    }
  ],
  Percentage: [
    {
      id: 'testsPassed',
      name: 'Tests passed',
      type: 'Percentage',
      icon: { set: 'streamline', name: 'percentage' }
    }
  ],
  Picklist: [
    {
      id: 'severity',
      name: 'Severity',
      type: 'Picklist',
      icon: { set: 'streamline', name: 'list' }
    }
  ],
  URL: [
    {
      id: 'backlog',
      name: 'Backlog',
      type: 'URL',
      icon: { set: 'streamline', name: 'chain' }
    }
  ],
  Location: [
    {
      id: 'location',
      name: 'Office location',
      type: 'Location',
      icon: { set: 'streamline', name: 'location' }
    }
  ],
  Attachment: [
    {
      id: 'attachments',
      name: 'Attachments',
      type: 'Attachment',
      icon: { set: 'streamline', name: 'paper-clip' }
    }
  ],
  'User Reference': [
    {
      id: 'verifiedBy',
      name: 'Verified by',
      type: 'User Reference',
      icon: { set: 'streamline', name: 'user' }
    }
  ],
  'Embedded field': [
    {
      id: 'release',
      name: 'Release',
      type: 'Embedded field',
      icon: { set: 'streamline', name: 'table-bolt' }
    }
  ],
  'Field reference': [
    {
      id: 'userStory',
      name: 'User story',
      type: 'Field reference',
      icon: { set: 'streamline', name: 'case' }
    },
    {
      id: 'upstreamDepBugs',
      name: 'Upstream dependency bugs',
      type: 'Field reference',
      icon: { set: 'streamline', name: 'case' }
    }
  ]
};

interface FieldReferenceHookReturn {
  parentItem: FieldReferenceMenuProps['parentItem'];
  menuLoading: FieldReferenceMenuProps['loading'];
  itemsToRender: FieldReferenceMenuItem[];
  altContexts: FieldReferenceMenuProps['altContexts'];
  currentContext: FieldReferenceMenuProps['currentContext'];
  handleAltContextClick: FieldReferenceInputProps['onAltContextClick'];
  selected: FieldReferenceInputProps['selected'];
  handleSelectedChange: FieldReferenceInputProps['onSelectedChange'];
  filterValue: FieldReferenceInputProps['filterValue'];
  setFilterValue: FieldReferenceInputProps['onFilterChange'];
  setMenuLoading: Dispatch<SetStateAction<boolean>>;
  setItemsShown: Dispatch<SetStateAction<number>>;
}

export const useFieldReferenceMock = (
  dataSet: 'static' | 'generated'
): FieldReferenceHookReturn => {
  const getMenuItems = dataSet === 'static' ? getStaticMenuItems : getGeneratedMenuItems;

  const [items, setItems] = useState(getMenuItems());
  const [itemsShown, setItemsShown] = useState(25);
  const [parentItem, setParentItem] = useState<FieldReferenceMenuProps['parentItem']>(undefined);
  const [menuLoading, setMenuLoading] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [selected, setSelected] = useState<Field[]>([]);
  const [altContexts, setAltContexts] = useState<
    FieldReferenceMenuProps['altContexts'] | undefined
  >(defaultAltContexts);
  const [currentContext, setCurrentContext] = useState(defaultContext);
  const selectionTimeoutRef = useRef<number | undefined>();
  const altContextTimeoutRef = useRef<number | undefined>();

  const itemsToRender = useMemo(() => {
    const applyFilter =
      selected.length === 0 ||
      (selected.at(-1)!.id !== parentItem?.id &&
        selected.at(-1)!.name.toLowerCase() !== filterValue.toLowerCase());
    const newItems = applyFilter
      ? fieldReferenceMenuHelpers.filterBySearch(items, filterValue)
      : items;

    return dataSet === 'static' ? newItems : newItems.slice(0, itemsShown);
  }, [filterValue, items, itemsShown, selected]);

  const handleSelectedChange: FieldReferenceInputProps['onSelectedChange'] = selectedValue => {
    window.clearTimeout(selectionTimeoutRef.current);
    window.clearTimeout(altContextTimeoutRef.current);
    setMenuLoading(false);

    if (!selectedValue.length) {
      setSelected([]);
      setItems(getMenuItems());
      setParentItem(undefined);
      setAltContexts(defaultAltContexts);
      setCurrentContext(defaultContext);
    } else {
      const lastItem = selectedValue.at(-1)!;
      setSelected(selectedValue);

      if (lastItem.items) {
        setAltContexts(undefined);
        setMenuLoading(true);
        setItems([]);
        setItemsShown(25);
        setParentItem(lastItem);

        // Fake timer to simulate an api request to update the menu items
        selectionTimeoutRef.current = window.setTimeout(() => {
          setMenuLoading(false);
          setItems(getMenuItems(lastItem));
        }, loadingTimeoutMS);
      } else {
        setItems(curr => fieldReferenceMenuHelpers.selectItem(curr, lastItem.id));
      }
    }
  };

  const handleAltContextClick: FieldReferenceInputProps['onAltContextClick'] = altContext => {
    window.clearTimeout(selectionTimeoutRef.current);
    window.clearTimeout(altContextTimeoutRef.current);

    setSelected([]);
    setAltContexts(undefined);
    setMenuLoading(true);
    setItems([]);
    setItemsShown(25);
    setParentItem(altContext);

    // Fake timer to simulate an api request to update the menu items
    altContextTimeoutRef.current = window.setTimeout(() => {
      setMenuLoading(false);
      setItems(getMenuItems(altContext));
      setCurrentContext({ ...altContext });
    }, loadingTimeoutMS);
  };

  return {
    parentItem,
    menuLoading,
    altContexts,
    currentContext,
    itemsToRender,
    handleAltContextClick,
    selected,
    handleSelectedChange,
    filterValue,
    setFilterValue,
    setMenuLoading,
    setItemsShown
  } as const;
};
